import type {GridFieldValue, RecordRow} from 'src/features/record-grid/lib/types';
import type {ViewableFile} from 'typings/portaro.be.types';
import type {FileService} from 'src/features/file/file.service';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import type {ToastMessageService} from 'shared/components/kp-toast-messages/toast-message.service';
import type RecordDataService from '../../../../../record/record.data-service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';
import {exists} from 'shared/utils/custom-utils';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {SUTOR_REPORT_STATES, SUTOR_REPORTS_APPROVING_SETTINGS} from '../../../../sutor-constants';
import {hasRecordReference} from 'src/features/record-grid/lib/types-utils';

export class SutorReportsApprovingService {
    public static serviceName = 'sutorReportsApprovingService';

    /*@ngInject*/
    constructor(private recordDataService: RecordDataService,
                private recordGridDataService: RecordGridDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private toastMessageService: ToastMessageService,
                private fileService: FileService) {
    }

    public async getReportReferencePdfFile(field: GridFieldValue | null): Promise<ViewableFile> {
        if (!exists(field) || !hasRecordReference(field)) {
            return null;
        }

        const referenceRecord = await this.recordDataService.getById(field.recordReference.id);
        if (!exists(referenceRecord)) {
            return null;
        }

        const allFiles = await this.fileService.getFilesByDirectory(referenceRecord.directoryId);
        return allFiles[0] ?? null;
    }

    public async getRefreshedReport(report: RecordRow): Promise<RecordRow | null> {
        try {
            return await this.recordGridDataService.loadSingleRow(report.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async approveReport(report: RecordRow, textNote: string | null): Promise<void> {
        try {
            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: report.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_APPROVING_SETTINGS.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REPORT_STATES.CONFIRMED}
                    },
                    {
                        record: report.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_APPROVING_SETTINGS.textNoteFieldId, [0, 0]),
                        setFieldValueRequest: {value: textNote ?? ''}
                    }
                ]
            });

            this.toastMessageService.showSuccess('Výkaz by schválen!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async disapproveReport(report: RecordRow, textNote: string | null): Promise<void> {
        try {
            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: report.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_APPROVING_SETTINGS.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REPORT_STATES.RETURNED}
                    },
                    {
                        record: report.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_APPROVING_SETTINGS.textNoteFieldId, [0, 0]),
                        setFieldValueRequest: {value: textNote ?? ''}
                    }
                ]
            });

            this.toastMessageService.showSuccess('Výkaz byl vrácen k dopracování!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }
}